import React from 'react';
import { Upload, Calendar, Clock } from 'lucide-react';
import GlassyDateTimePicker from '../../../components/GlassyDateTimePicker';

const formatFileSize = (bytes = 0) => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
};

const EventDetailsStep = ({
  formData,
  handleInputChange,
  onImageUpload,
  imageUploadState,
  errors
}) => (
  <div className="form-section">
    <h3>⚙️ Event Details</h3>
    <p>Configure the schedule, logistics, and visual identity of your event.</p>

    {/* --- 1. Event Schedule --- */}
    <div className="form-section-header" style={{ marginTop: '1.5rem' }}>
      <h4>📅 Event Schedule</h4>
      <p className="section-subtitle">When is the event happening?</p>
    </div>

    <div className="form-grid">
      <div className="form-field required">
        <label><Calendar size={14} /> Event Start Date</label>
        <GlassyDateTimePicker
          mode="date"
          value={formData.eventDate}
          onChange={(val) => handleInputChange('eventDate', val)}
          placeholder="Select start date"
        />
        {errors.eventDate && <div className="error-message">{errors.eventDate}</div>}
        <div className="hint-text">Must be in the future</div>
      </div>

      <div className="form-field required">
        <label><Clock size={14} /> Event Start Time</label>
        <GlassyDateTimePicker
          mode="time"
          value={formData.eventTime}
          onChange={(val) => handleInputChange('eventTime', val)}
          placeholder="Select start time"
        />
        {errors.eventTime && <div className="error-message">{errors.eventTime}</div>}
      </div>
    </div>

    <div className="form-grid">
      <div className="form-field required">
        <label><Calendar size={14} /> Event End Date</label>
        <GlassyDateTimePicker
          mode="date"
          value={formData.endDate}
          onChange={(val) => handleInputChange('endDate', val)}
          placeholder="Select end date"
        />
        {errors.endDate && <div className="error-message">{errors.endDate}</div>}
      </div>

      <div className="form-field required">
        <label><Clock size={14} /> Event End Time</label>
        <GlassyDateTimePicker
          mode="time"
          value={formData.endTime}
          onChange={(val) => handleInputChange('endTime', val)}
          placeholder="Select end time"
        />
        {errors.endTime && <div className="error-message">{errors.endTime}</div>}
        <div className="hint-text">Must be after start</div>
      </div>
    </div>

    <div className="form-grid">
      <div className="form-field">
        <label><Clock size={14} /> Doors Open Time (optional)</label>
        <GlassyDateTimePicker
          mode="time"
          value={formData.doorsOpenTime}
          onChange={(val) => handleInputChange('doorsOpenTime', val)}
          placeholder="Select doors open time"
        />
        {errors.doorsOpenTime && <div className="error-message">{errors.doorsOpenTime}</div>}
      </div>
      {/* Empty slot for alignment if needed, or could span full width if we wanted */}
      <div className="form-field"></div>
    </div>

    {/* --- 2. Sales Window --- */}
    <div className="form-section-header">
      <h4>🎟️ Sales Window</h4>
      <p className="section-subtitle">Define when tickets are available for purchase.</p>
    </div>

    <div className="form-grid">
      <div className="form-field">
        <label>Ticket Sales Start</label>
        <GlassyDateTimePicker
          mode="datetime"
          value={formData.ticketSalesStart}
          onChange={(val) => handleInputChange('ticketSalesStart', val)}
          placeholder="Select sales start"
        />
      </div>

      <div className="form-field">
        <label>Ticket Sales End</label>
        <GlassyDateTimePicker
          mode="datetime"
          value={formData.ticketSalesEnd}
          onChange={(val) => handleInputChange('ticketSalesEnd', val)}
          placeholder="Select sales end"
        />
      </div>
    </div>

    {/* --- 3. Logistics & Info --- */}
    <div className="form-section-header">
      <h4>📋 Logistics & Info</h4>
      <p className="section-subtitle">Important details for your attendees.</p>
    </div>

    <div className="form-grid">
      <div className="form-field">
        <label>Age Restriction</label>
        <input
          type="text"
          value={formData.ageRestriction}
          onChange={(e) => handleInputChange('ageRestriction', e.target.value)}
          placeholder="e.g., All Ages, 18+, 21+"
          maxLength={255}
          className="wizard-input"
        />
        <div className={`character-counter ${formData.ageRestriction?.length > 200 ? 'warning' : ''}`}>
          {formData.ageRestriction?.length || 0}/255
        </div>
      </div>

      <div className="form-field">
        <label>Dress Code</label>
        <input
          type="text"
          value={formData.dressCode}
          onChange={(e) => handleInputChange('dressCode', e.target.value)}
          placeholder="e.g., Smart Casual, Black Tie"
          maxLength={255}
          className="wizard-input"
        />
        <div className={`character-counter ${formData.dressCode?.length > 200 ? 'warning' : ''}`}>
          {formData.dressCode?.length || 0}/255
        </div>
      </div>
    </div>

    <div className="form-field">
      <label>Special Instructions</label>
      <textarea
        value={formData.specialInstructions}
        onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
        placeholder="Parking info, accessibility details, or what to bring..."
        className="wizard-textarea"
      />
    </div>

    {/* --- 4. Media --- */}
    <div className="form-section-header">
      <h4>🖼️ Media</h4>
      <p className="section-subtitle">Visuals to attract attendees.</p>
    </div>

    <div className="form-field required">
      <label>Event Image</label>
      <div className="upload-tile">
        <input
          type="file"
          accept="image/png, image/jpeg, image/webp, image/gif"
          onChange={(e) => onImageUpload(e.target.files?.[0] || null)}
          disabled={imageUploadState?.isUploading}
        />

        <div className="upload-row">
          <div className="upload-icon" aria-hidden="true"><Upload size={24} /></div>
          <div>
            <div className="upload-meta">Upload a standout cover</div>
            <div className="upload-subtle">Bright, high-contrast images get more clicks.</div>
          </div>
        </div>

        <div className="upload-pills">
          <span className="upload-pill">JPG / PNG / WEBP</span>
          <span className="upload-pill">Max 5MB</span>
          <span className="upload-pill">Rec. 1600x900</span>
        </div>

        {imageUploadState?.fileName && (
          <div className="upload-row" style={{ marginTop: '0.35rem' }}>
            <div className={`file-chip ${imageUploadState?.error ? 'error' : ''}`}>
              <span className="name">{imageUploadState.fileName}</span>
              {imageUploadState?.fileSize ? (
                <span className="size">• {formatFileSize(imageUploadState.fileSize)}</span>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {(imageUploadState?.previewUrl || formData.imageUrl) && (
        <div className="image-preview">
          <img
            src={imageUploadState?.previewUrl || formData.imageUrl}
            alt="Event preview"
          />
        </div>
      )}

      {imageUploadState?.isUploading && (
        <div className="upload-status">
          <span>⏳ Uploading image...</span>
        </div>
      )}

      {imageUploadState?.error && (
        <div className="upload-status error">
          <span>⚠ {imageUploadState.error}</span>
        </div>
      )}

      {errors?.imageUrl && (
        <p className="error-message">{errors.imageUrl}</p>
      )}
    </div>

    {/* --- 5. Financials & Marketing --- */}
    <div className="form-section-header">
      <h4>💰 Financials & Growth</h4>
      <p className="section-subtitle">Manage revenue settings and boost visibility.</p>
    </div>

    <div className="financial-wrapper">
      {/* Left: Fees & Taxes */}
      <div className="financial-card">
        <h5>
          <span style={{ color: '#4ade80' }}>$</span> Revenue Settings
        </h5>
        <p>Configure fees passed to attendees.</p>

        <div className="financial-row">
          <div className="fin-input-box">
            <span className="fin-label">Service Fee</span>
            <div className="fin-input-wrapper">
              <span className="fin-prefix">$</span>
              <input
                className="fin-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={formData.serviceFee || ''}
                onChange={(e) => handleInputChange('serviceFee', e.target.value)}
              />
            </div>
          </div>

          <div className="fin-input-box">
            <span className="fin-label">Tax Rate</span>
            <div className="fin-input-wrapper">
              <input
                className="fin-input"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="0.0"
                value={formData.tax || ''}
                onChange={(e) => handleInputChange('tax', e.target.value)}
              />
              <span className="fin-suffix">%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Featured */}
      <div className="featured-card-integrated">
        <div>
          <span className="featured-badge">Marketing</span>
          <div className="featured-header">
            <div>
              <h5 style={{ margin: '0 0 4px', fontSize: '1.1rem', color: '#fff' }}>Featured Event</h5>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(253, 224, 71, 0.7)' }}>
                Pin to homepage "Featured" rail.
              </p>
            </div>
            <div
              className={`featured-toggle-switch ${formData.isFeatured ? 'active' : ''}`}
              onClick={() => handleInputChange('isFeatured', !formData.isFeatured)}
            />
          </div>
        </div>

        {formData.isFeatured && (
          <div style={{
            marginTop: '16px',
            fontSize: '0.85rem',
            color: '#fde047',
            background: 'rgba(253, 224, 71, 0.1)',
            padding: '8px 12px',
            borderRadius: '8px'
          }}>
            ✨ This event will be highlighted for maximum visibility.
          </div>
        )}
      </div>
    </div>
  </div>
);

export default EventDetailsStep;
